import { useState } from "react";
import { SortingState } from "@tanstack/react-table";
import {
  RequestFiltersComponent as RequestFilters,
  RequestsTable,
  RejectRequestDialog,
} from "../components/organisms";
import type { CredentialRequest } from "../types";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";

interface CredentialRequestListTemplateProps {
  // UI
  title?: string;
  subtitle?: string;
  
  // Data
  requests: CredentialRequest[];
  districts: District[];
  regions: Region[];

  // State
  isLoading: boolean;
  isSubmitting: boolean;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  // Search & Sort
  searchValue: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;

  // Filters
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  districtFilter: string;
  onDistrictFilterChange: (value: string) => void;
  regionFilter: string;
  onRegionFilterChange: (value: string) => void;
  dateFromFilter: string;
  onDateFromFilterChange: (value: string) => void;
  dateToFilter: string;
  onDateToFilterChange: (value: string) => void;

  // Actions
  onRowClick: (request: CredentialRequest) => void;
  onApprove: (request: CredentialRequest) => void;
  onReject: (requestId: string, notes: string) => void;
  onMarkPrinted: (request: CredentialRequest) => void;
  onMarkDelivered: (request: CredentialRequest) => void;
  onCreateNew: () => void;
  
  // Options
  columnOptions?: {
    hideDistrict?: boolean;
    hideRegion?: boolean;
  };
  filterOptions?: {
    hideDistrictRegion?: boolean;
  };
  hideHeader?: boolean;
}

/**
 * Template: Layout for credential request list page
 */
export function CredentialRequestListTemplate({
  title = "Solicitudes de Credenciales",
  subtitle = "Gestiona las solicitudes de credenciales ministeriales",
  requests,
  districts,
  regions,
  isLoading,
  isSubmitting,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  sorting,
  onSortingChange,
  statusFilter,
  onStatusFilterChange,
  districtFilter,
  onDistrictFilterChange,
  regionFilter,
  onRegionFilterChange,
  dateFromFilter,
  onDateFromFilterChange,
  dateToFilter,
  onDateToFilterChange,
  onRowClick,
  onApprove,
  onReject,
  onMarkPrinted,
  onMarkDelivered,
  onCreateNew,
  columnOptions,
  filterOptions,
  hideHeader = false,
}: CredentialRequestListTemplateProps) {
  // Reject dialog state
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [requestToReject, setRequestToReject] = useState<CredentialRequest | null>(null);

  const handleRejectClick = (request: CredentialRequest) => {
    setRequestToReject(request);
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = (notes: string) => {
    if (requestToReject) {
      onReject(requestToReject.id, notes);
      setShowRejectDialog(false);
      setRequestToReject(null);
    }
  };  return (
    <div className="container mx-auto py-6">
      {/* Page Header */}
      {!hideHeader && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
      )}

      {/* Filters */}
      {!filterOptions?.hideDistrictRegion && (
        <div className="mb-6">
          <RequestFilters
            statusFilter={statusFilter}
            onStatusChange={onStatusFilterChange}
            districtFilter={districtFilter}
            onDistrictChange={onDistrictFilterChange}
            regionFilter={regionFilter}
            onRegionChange={onRegionFilterChange}
            dateFromFilter={dateFromFilter}
            onDateFromChange={onDateFromFilterChange}
            dateToFilter={dateToFilter}
            onDateToChange={onDateToFilterChange}
            districts={districts}
            regions={regions}
          />
        </div>
      )}

      {/* Filters - Status and Date Only (for INSTITUTION/Function) */}
      {filterOptions?.hideDistrictRegion && (
        <div className="mb-6">
          <RequestFilters
            statusFilter={statusFilter}
            onStatusChange={onStatusFilterChange}
            districtFilter=""
            onDistrictChange={() => {}}
            regionFilter=""
            onRegionChange={() => {}}
            dateFromFilter={dateFromFilter}
            onDateFromChange={onDateFromFilterChange}
            dateToFilter={dateToFilter}
            onDateToChange={onDateToFilterChange}
            districts={[]}
            regions={[]}
            isScoped={true}
          />
        </div>
      )}

      {/* Table */}
      <RequestsTable
        requests={requests}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[20, 50, 100]}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onRowClick={onRowClick}
        onApprove={onApprove}
        onRejectClick={handleRejectClick}
        onMarkPrinted={onMarkPrinted}
        onMarkDelivered={onMarkDelivered}
        onCreateNew={onCreateNew}
        columnOptions={columnOptions}
      />

      {/* Reject Dialog */}
      <RejectRequestDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        isSubmitting={isSubmitting}
        onConfirm={handleRejectConfirm}
      />
    </div>
  );
}
